import { Box } from '@tanium/react-box';
import { FormRow } from '@tanium/react-form';
import { FormSelectInput } from '@tanium/react-form-select-input';
import { Stack } from '@tanium/react-stack';
import { useTheme } from '@tanium/react-theme-context';
import { Field, FieldProps, FormikErrors } from 'formik';
import get from 'lodash.get';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BastionTargetAccess } from 'gql/BastionTargetAccess';
import { TARGET_ACCESS_ID } from 'components/pages/request/requestPartials/createRequestFieldsPartials/AccessType';
import { IIconTypes } from 'components/icons/StatusIcon';
import BastionType from 'gql/BastionType';
import Tooltip from 'components/tooltip/Tooltip';

export const BASTION_TYPE = 'bastionType';

interface IProps {
  bastionTargetAccess: BastionTargetAccess[];
}

export const BastionAccess = ({ bastionTargetAccess }: IProps): React.ReactElement => {
  const formInputIdPrefix = 'create-request-';
  const { t } = useTranslation();
  const theme = useTheme();

  const options = bastionTargetAccess.map((access) => {
    return {
      label: access.name,
      value: access.id,
      description: access.description,
      statusIcon: access.bastionType === BastionType.PUBLIC ? IIconTypes.WARNING : undefined,
    };
  });

  return (
    <Box display="inline-flex" mr={1}>
      <Field name={TARGET_ACCESS_ID}>
        {({ field, form }: FieldProps): React.ReactElement => (
          <FormRow
            label={t('BASTION_TYPE')}
            required
            description={
              <Box>
                <Box>{t('BASTION_TYPE_DESCRIPTION')}</Box>
              </Box>
            }
            errors={(get(form.touched, field.name) && get(form.errors, field.name)) as FormikErrors<string>}
            labelId={`${formInputIdPrefix}${field.name}`}
            data-testid={`${formInputIdPrefix}${field.name}`}
          >
            <FormSelectInput
              onChange={(option): void => {
                void form.setFieldValue(field.name, option.value);
              }}
              value={options?.find((option) => option.value === field.value)}
              inputId={`${formInputIdPrefix}${field.name}`}
              options={options}
              aria-label={t('BASTION_TYPE')}
              formatOptionLabel={(
                option: {
                  value: string;
                  label: string;
                  description: string;
                  statusIcon?: IIconTypes;
                },
                state
              ) => {
                const isSelected =
                  state.context === 'menu' && Array.isArray(state.selectValue) && option === state.selectValue[0];
                /* istanbul ignore next */
                const subtextColor = theme.colors[isSelected ? 'subdued' : 'subtle'];
                return (
                  <Stack direction="column" itemSpacing={0.5}>
                    <Box>{option.label}</Box>
                    <Tooltip content={option.description}>
                      <Box color={subtextColor} fontSize={11}>
                        {option.description}
                      </Box>
                    </Tooltip>
                  </Stack>
                );
              }}
              styles={{
                singleValue: (base) => ({
                  ...base,
                  marginTop: theme.space[1],
                  marginBottom: theme.space[1],
                  position: 'relative',
                  transform: 'none',
                  whiteSpace: 'normal',
                  maxHeight: '3.75em',
                }),
                input: (base) => ({
                  ...base,
                  position: 'absolute',
                  top: theme.space[1] - 2,
                  paddingTop: 0,
                }),
              }}
            />
          </FormRow>
        )}
      </Field>
    </Box>
  );
};

export default BastionAccess;
